/**
 * §P.10 · Competición: campeonatos, convocatoria, resultados, peso y licencias.
 *
 * **El entrenador entra en todo esto**, al contrario que en los informes: quien lleva
 * a los niños al campeonato el sábado es él. Las licencias federativas son la
 * excepción —`own` y `adm`—, porque son papeles y dinero.
 */

export type NivelDeCampeonato =
  'club' | 'local' | 'provincial' | 'autonomico' | 'nacional' | 'internacional';

export type Medalla = 'oro' | 'plata' | 'bronce';

export type EstadoDeInscripcion =
  'proposed' | 'confirmed' | 'registered' | 'withdrawn' | 'competed';

export interface Campeonato {
  readonly id: string;
  readonly organizer_academy_id: string | null;
  /** Si lo organiza este club. Los ajenos se ven y no se editan. */
  readonly is_mine: boolean;
  readonly discipline_id: string;
  readonly discipline_name: string;
  readonly name: string;
  readonly federation: string | null;
  readonly level: NivelDeCampeonato | null;
  readonly starts_on: string | Date;
  readonly ends_on: string | Date | null;
  readonly city: string | null;
  readonly venue_name: string | null;
  readonly registration_deadline: string | Date | null;
  readonly is_public: boolean;
  readonly external_album_url: string | null;
  readonly registered_count: number;
  readonly medals: number;
  readonly created_at: string | Date;
}

export interface CategoriaDeCampeonato {
  readonly id: string;
  readonly competition_id: string;
  readonly name: string;
  readonly modality: string | null;
  readonly gender: 'f' | 'm' | 'mixed' | null;
  readonly age_min: number | null;
  readonly age_max: number | null;
  readonly belt_min_order: number | null;
  readonly belt_max_order: number | null;
  readonly weight_min_kg: number | null;
  readonly weight_max_kg: number | null;
}

export interface Inscripcion {
  readonly id: string;
  readonly competition_id: string;
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly category_id: string | null;
  readonly category_name: string | null;
  readonly status: EstadoDeInscripcion;
  readonly confirmed_by: string | null;
  /** El peso **del pesaje oficial**, distinto del control interno del club. */
  readonly weigh_in_kg: number | null;
  readonly fee_cents: number | null;
  readonly notes: string | null;
  readonly placement: number | null;
  readonly medal: Medalla | null;
  readonly bouts_won: number | null;
  readonly bouts_lost: number | null;
  readonly created_at: string | Date;
}

export interface CategoriaSugerida {
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly age: number | null;
  readonly weight_kg: number | null;
  /** Sin esto no se sabe si el peso sirve: uno de hace tres meses, no. */
  readonly weighed_on: string | Date | null;
  readonly belt_order: number | null;
  readonly category_id: string | null;
  readonly category_name: string | null;
  readonly alternatives: number;
  /** Por qué encaja o por qué no. **Salen todos, también los que no encajan.** */
  readonly reason: string;
}

export interface PesoDelAlumno {
  readonly id: string;
  readonly student_id: string;
  readonly weight_kg: number;
  readonly measured_on: string | Date;
  readonly competition_id: string | null;
  readonly competition_name: string | null;
  readonly recorded_by: string | null;
  readonly recorded_by_name: string | null;
  /**
   * Diferencia con el peso anterior.
   *
   * **Es la cifra que importa**: un peso suelto no dice nada, y tres kilos menos en
   * dos semanas antes de un campeonato es exactamente lo que un maestro tiene que
   * ver.
   */
  readonly delta_kg: number | null;
  readonly created_at: string | Date;
}

export type EstadoDeLicencia = 'pending' | 'active' | 'expired' | 'cancelled';

export interface LicenciaFederativa {
  readonly id: string;
  readonly student_id: string;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly federation: string;
  readonly license_number: string | null;
  readonly season: string | null;
  readonly valid_from: string | Date | null;
  readonly valid_to: string | Date | null;
  readonly fee_cents: number | null;
  readonly status: EstadoDeLicencia;
  /**
   * Negativo si ya caducó.
   *
   * **Sin licencia en vigor el alumno no compite**, y eso se descubre el sábado por
   * la mañana en la mesa de inscripciones. Por eso la pantalla ordena por esto.
   */
  readonly days_to_expiry: number | null;
  readonly created_at: string | Date;
}

export const NIVELES: readonly {
  id: NivelDeCampeonato;
  etiqueta: string;
}[] = [
  { id: 'club', etiqueta: 'Del club' },
  { id: 'local', etiqueta: 'Local' },
  { id: 'provincial', etiqueta: 'Provincial' },
  { id: 'autonomico', etiqueta: 'Autonómico' },
  { id: 'nacional', etiqueta: 'Nacional' },
  { id: 'internacional', etiqueta: 'Internacional' },
];

const NOMBRES_DE_NIVEL: Readonly<Record<string, string>> = Object.fromEntries(
  NIVELES.map((n) => [n.id, n.etiqueta])
);

export const etiquetaDeNivel = (n: string | null): string =>
  n === null ? 'Sin nivel' : (NOMBRES_DE_NIVEL[n] ?? n);

/**
 * Los cinco estados de una inscripción, dichos como los diría el maestro.
 *
 * `proposed` es «propuesto» —el club lo ha pensado— y `confirmed` es que la familia
 * ha dicho que sí. Entre esos dos está la mitad del trabajo de la semana anterior.
 */
export const ESTADOS_DE_INSCRIPCION: Readonly<
  Record<EstadoDeInscripcion, string>
> = {
  proposed: 'Propuesto',
  confirmed: 'La familia ha dicho que sí',
  registered: 'Inscrito en la federación',
  withdrawn: 'Retirado',
  competed: 'Compitió',
};

export const ESTADOS_DE_LICENCIA: Readonly<Record<EstadoDeLicencia, string>> = {
  pending: 'Pendiente',
  active: 'En vigor',
  expired: 'Caducada',
  cancelled: 'Anulada',
};

export const MEDALLAS: readonly { id: Medalla; etiqueta: string }[] = [
  { id: 'oro', etiqueta: 'Oro' },
  { id: 'plata', etiqueta: 'Plata' },
  { id: 'bronce', etiqueta: 'Bronce' },
];

/**
 * Cómo de urgente es una licencia, en color.
 *
 * Negativo es caducada —rojo, porque ese alumno no compite— y menos de treinta días
 * es ámbar: renovar una licencia federativa no es inmediato y descubrirlo el viernes
 * no sirve de nada.
 */
export function urgenciaDeLicencia(dias: number | null): string {
  if (dias === null) return 'pastilla-left';
  if (dias < 0) return 'pastilla-mal';
  if (dias <= 30) return 'pastilla-espera';
  return 'pastilla-bien';
}

/** «Caducó hace 5 días» / «Le quedan 12 días». */
export function cuantoQuedaDeLicencia(dias: number | null): string {
  if (dias === null) return 'Sin fecha de caducidad';
  if (dias < 0) {
    const pasados = Math.abs(dias);
    return `Caducó hace ${pasados} ${pasados === 1 ? 'día' : 'días'}`;
  }
  if (dias === 0) return 'Caduca hoy';
  return `${dias === 1 ? 'Le queda 1 día' : `Le quedan ${dias} días`}`;
}
